import React, { useRef, useState } from 'react';
import './PeriodicUpdateToolPopup.scss';
import Rodal from '../../../components/Rodal/Rodal';
import {
    FormGroup,
    Input,
    InputGroupAddon,
    ModalBody,
    ModalHeader,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import { PutQuestionsPeriodUpdateContract } from '../../../models/DataRequestHub/PutQuestionsPeriodUpdateContract';

export interface PeriodicUpdateToolPopupProps {
    projectId: number;
    dataFormId: number;
    periodicityOptions: string[];
    close(): void;
    onSuccessfulUpdate(): void;
}

export const PeriodicUpdateToolPopup = (
    props: PeriodicUpdateToolPopupProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [dateFrom, setDateFrom] = useState<Date>(null);
    const [dateTo, setDateTo] = useState<Date>(null);
    const [selectedPeriodicities, setSelectedPeriodicities] = useState<
        string[]
    >([]);
    const [isLoading, setIsLoading] = useState(false);
    const dateFromRef = useRef(null);
    const dateToRef = useRef(null);

    const togglePeriodicity = (periodicity: string, isChecked: boolean) => {
        setSelectedPeriodicities((prev) =>
            isChecked
                ? [...prev, periodicity]
                : prev.filter((item) => item !== periodicity)
        );
    };

    const isPeriodValid = !!dateFrom && !!dateTo && dateFrom <= dateTo;
    const isApplyDisabled =
        isLoading || !isPeriodValid || selectedPeriodicities.length === 0;

    const applyUpdate = () => {
        setIsLoading(true);

        const requestObj: PutQuestionsPeriodUpdateContract = {
            dateFrom: moment(dateFrom).format('YYYY-MM-DD'),
            dateTo: moment(dateTo).format('YYYY-MM-DD'),
            periodicities: selectedPeriodicities,
        };

        axios
            .put(
                `/api/DataRequestProject/${props.projectId}/DataForm/${props.dataFormId}/Questions/PeriodUpdate`,
                requestObj
            )
            .then((response) => {
                if (response.status === 200) {
                    createNotification(
                        'Periodic update applied successfully',
                        'success'
                    );
                    props.onSuccessfulUpdate();
                    props.close();
                } else {
                    createNotification(
                        'An error occured while applying periodic update',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An error occured while applying periodic update',
                    'error'
                );
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <div className="periodic-update-tool-popup">
            <Rodal
                visible
                onClose={() => {
                    if (!isLoading) {
                        props.close();
                    }
                }}
                animation="slideUp"
                className="periodic-update-tool-modal"
                showMask={true}
                width={500}
                showCloseButton={true}>
                <ModalHeader>Periodic Update Tool</ModalHeader>
                <ModalBody>
                    <div className="period-section">
                        <label className="section-title">
                            Period to request
                        </label>
                        <div className="period-inputs">
                            <FormGroup className="d-flex align-items-center small-border">
                                <InputGroupAddon
                                    addonType="prepend"
                                    onClick={() =>
                                        dateFromRef.current?.setOpen(true)
                                    }>
                                    <div className="input-group-text">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                    </div>
                                </InputGroupAddon>
                                <DatePicker
                                    portalId="periodic-update-tool-date-portal"
                                    ref={dateFromRef}
                                    selected={dateFrom}
                                    maxDate={dateTo}
                                    name="dateFrom"
                                    placeholderText="mm/dd/yyyy"
                                    dateFormat="MM/dd/yyyy"
                                    disabled={isLoading}
                                    popperProps={{ strategy: 'fixed' }}
                                    onChange={(value: Date) =>
                                        setDateFrom(value)
                                    }
                                />
                            </FormGroup>
                            <span className="period-separator">to</span>
                            <FormGroup className="d-flex align-items-center small-border">
                                <InputGroupAddon
                                    addonType="prepend"
                                    onClick={() =>
                                        dateToRef.current?.setOpen(true)
                                    }>
                                    <div className="input-group-text">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                    </div>
                                </InputGroupAddon>
                                <DatePicker
                                    portalId="periodic-update-tool-date-portal"
                                    ref={dateToRef}
                                    selected={dateTo}
                                    minDate={dateFrom}
                                    name="dateTo"
                                    placeholderText="mm/dd/yyyy"
                                    dateFormat="MM/dd/yyyy"
                                    disabled={isLoading}
                                    popperProps={{ strategy: 'fixed' }}
                                    onChange={(value: Date) => setDateTo(value)}
                                />
                            </FormGroup>
                        </div>
                        <div className="helper-text">
                            Extends each item&apos;s period to include the new
                            months.
                        </div>
                    </div>
                    <div className="periodicities-section">
                        {props.periodicityOptions.map((option) => (
                            <div className="checkbox-field" key={option}>
                                <Input
                                    type="checkbox"
                                    checked={selectedPeriodicities.includes(
                                        option
                                    )}
                                    disabled={isLoading}
                                    className="checkbox-input"
                                    onChange={(event) =>
                                        togglePeriodicity(
                                            option,
                                            event.target.checked
                                        )
                                    }
                                />
                                <label>{`Apply to ${option} items`}</label>
                            </div>
                        ))}
                    </div>
                    <div className="reset-status-hint">
                        Matched items will be reset to New status.
                    </div>
                    <div className="buttons-wrapper">
                        <button
                            type="button"
                            className="btn btn-cancel"
                            disabled={isLoading}
                            onClick={props.close}>
                            Cancel
                        </button>
                        <ButtonLoader
                            buttonText="Apply Update"
                            loaderButtonText=""
                            disabled={isApplyDisabled}
                            isLoading={isLoading}
                            onClick={applyUpdate}
                            className="btn btn-primary"
                        />
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default PeriodicUpdateToolPopup;
